import { useLang } from "@/lib/lang";
import { LegalLayout, Section, BulletList } from "@/components/LegalLayout";

export default function Privacy() {
  const { lang } = useLang();
  const T = lang === "fr" ? FR : EN;

  return (
    <LegalLayout title={T.title}>
      <h1 className="font-serif-mystery text-4xl md:text-5xl text-zinc-900 mb-2" data-testid="privacy-title">{T.title}</h1>
      <p className="font-mono-data text-xs text-zinc-500 mb-8">{T.lastUpdate}</p>

      <p className="text-zinc-700 leading-relaxed mb-6">{T.welcome}</p>

      <Section title={T.s1.title}>
        <p>{T.s1.body}</p>
        <BulletList items={T.s1.items} />
      </Section>

      <Section title={T.s2.title}>
        <p>{T.s2.body}</p>
        <BulletList items={T.s2.items} />
      </Section>

      <Section title={T.s3.title}>
        {T.s3.body.map((p, i) => <p key={i}>{p}</p>)}
      </Section>

      <Section title={T.s4.title}>
        <p>{T.s4.body}</p>
      </Section>

      <Section title={T.s5.title}>
        <p>{T.s5.body}</p>
      </Section>

      <Section title={T.sGeo.title}>
        <p>{T.sGeo.body}</p>
        <BulletList items={T.sGeo.items} />
      </Section>

      <Section title={T.contact.title}>
        <p>{T.contact.body}</p>
        <p className="font-mono-data text-sm"><span className="text-zinc-500">{T.contact.emailLabel} :</span> <a href="mailto:BLEUREEFOBERMAYR@GMAIL.COM" className="text-[#8b6f3d] hover:underline">BLEUREEFOBERMAYR@GMAIL.COM</a></p>
      </Section>
    </LegalLayout>
  );
}

const FR = {
  title: "Politique de confidentialité",
  lastUpdate: "Dernière mise à jour : 2026",
  welcome: "Bienvenue sur The Only Map You Will Ever Need. Nous respectons votre vie privée et nous nous engageons à protéger les informations personnelles des visiteurs de notre site.",
  s1: { title: "Informations collectées", body: "Nous pouvons collecter certaines informations lorsque vous utilisez notre site, notamment :",
        items: ["Les informations envoyées volontairement via un formulaire de contact.",
                "Les commentaires laissés sur certaines pages.",
                "Des données techniques comme le navigateur utilisé, le type d'appareil et les statistiques de visite."] },
  s2: { title: "Utilisation des informations", body: "Les informations collectées peuvent être utilisées pour :",
        items: ["Améliorer l'expérience utilisateur.",
                "Répondre aux messages envoyés.",
                "Analyser la fréquentation du site.",
                "Améliorer notre contenu et nos services."] },
  s3: { title: "Cookies et publicité",
        body: [
          "Notre site peut utiliser des cookies afin d'améliorer votre expérience.",
          "Nous pouvons utiliser des services publicitaires comme Google AdSense, qui peuvent utiliser des cookies afin d'afficher des annonces adaptées aux intérêts des visiteurs.",
          "Les visiteurs peuvent gérer leurs préférences concernant les cookies dans les paramètres de leur navigateur."
        ] },
  s4: { title: "Liens externes", body: "Notre site peut contenir des liens vers des sites externes. Nous ne sommes pas responsables du contenu ou des pratiques de confidentialité de ces sites." },
  s5: { title: "Protection des données", body: "Nous prenons des mesures raisonnables afin de protéger les informations recueillies. Cependant, aucune transmission sur Internet ne peut être garantie comme totalement sécurisée." },
  sGeo: {
    title: "Géolocalisation et Mode Aventurier",
    body: "Lorsque vous utilisez le Mode Aventurier pour valider une visite, votre position GPS est utilisée uniquement pour vérifier que vous vous trouvez à moins de 50 mètres du lieu. Elle est enregistrée avec votre visite mais n'est jamais partagée avec des tiers.",
    items: [
      "La géolocalisation est optionnelle et nécessite votre autorisation explicite dans le navigateur.",
      "Les publications de commentaires, photos et vidéos sur un lieu sont réservées aux utilisateurs ayant une visite validée par GPS.",
      "Les contenus publiés (commentaires, photos, vidéos) sont automatiquement analysés par une intelligence artificielle de modération avant publication afin de bloquer insultes, propos haineux, harcèlement, spam, contenus adultes ou illégaux.",
      "Vous pouvez retirer votre autorisation de géolocalisation à tout moment dans les paramètres de votre navigateur.",
    ],
  },
  contact: { title: "Contact", body: "Pour toute question concernant cette politique de confidentialité :", emailLabel: "Courriel" },
};

const EN = {
  title: "Privacy Policy",
  lastUpdate: "Last update: 2026",
  welcome: "Welcome to The Only Map You Will Ever Need. We respect your privacy and are committed to protecting the personal information of visitors to our site.",
  s1: { title: "Information collected", body: "We may collect certain information when you use our site, including:",
        items: ["Information voluntarily sent via a contact form.",
                "Comments left on certain pages.",
                "Technical data such as browser, device type and visit statistics."] },
  s2: { title: "Use of information", body: "The information collected may be used to:",
        items: ["Improve user experience.",
                "Reply to messages sent.",
                "Analyze site traffic.",
                "Improve our content and services."] },
  s3: { title: "Cookies and advertising",
        body: [
          "Our site may use cookies to improve your experience.",
          "We may use advertising services such as Google AdSense, which may use cookies to display ads tailored to visitors' interests.",
          "Visitors can manage their cookie preferences in their browser settings."
        ] },
  s4: { title: "External links", body: "Our site may contain links to external sites. We are not responsible for the content or privacy practices of those sites." },
  s5: { title: "Data protection", body: "We take reasonable measures to protect the information collected. However, no transmission over the Internet can be guaranteed to be totally secure." },
  sGeo: {
    title: "Geolocation & Adventurer Mode",
    body: "When you use Adventurer Mode to validate a visit, your GPS position is used solely to verify that you are within 50 meters of the location. It is stored with your visit record and is never shared with third parties.",
    items: [
      "Geolocation is optional and requires your explicit browser permission.",
      "Posting comments, photos or videos on a location is reserved for users with a GPS-validated visit.",
      "Published content (comments, photos, videos) is automatically screened by AI moderation before publication to block insults, hate speech, harassment, spam, adult or illegal content.",
      "You can withdraw geolocation permission at any time in your browser settings.",
    ],
  },
  contact: { title: "Contact", body: "For any question about this privacy policy:", emailLabel: "Email" },
};
